import type { ReactNode } from "react";

/**
 * Breathing placeholder for content that is still loading (UI_CONVENTIONS →
 * Motion, `.skeleton`). Size and shape come from the caller so a skeleton can
 * mirror the element it stands in for; that mirroring is the whole point, a
 * generic grey block tells the user less than the layout they are about to get.
 *
 * Always `aria-hidden`: the boxes carry no information. Wrap a skeleton layout
 * in `<SkeletonRegion>` so assistive tech hears the wait once instead of
 * nothing at all.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={className ? `skeleton ${className}` : "skeleton"} />;
}

/**
 * Announces a loading region once, for a subtree of `aria-hidden` skeletons.
 * `aria-busy` marks the region as in-flight; `label` is the sr-only text, so it
 * must be a translated string (ADR-0013).
 */
export function SkeletonRegion({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
